import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ButtonColor } from '@videoblocks/storywind';

import {
  selectAuthPermissions,
  selectHasAnySubscription,
  selectIsLoggedIn,
} from '../../auth/AuthSelectors';
import AbstractAPI from '../../common/AbstractAPI';
import SiteConstants, {
  BlogResourceTypes,
} from '../../common/SiteConstants/SiteConstants';
import GenericFAQ from '../../common/components/GenericFAQ';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import { ADOBE_PLUGIN_MARKETPLACE_URL } from '../AdobeConstants';
import ImageAndText from '../components/ImageAndText';
import MultilineTipsAndTutorialsCard from '../components/MultilineTipsAndTutorialsCard';
import UnlockCreativeFreedom from '../components/UnlockCreativeFreedom';
import useUpgradeUrl from '../hooks/useUpgradeUrl';

export const PremiereProPluginLandingPageContainer = () => {
  const pricingUrl = '/pricing';

  const [ctaInfo, setCTAInfo] = useState<{ text: string; link: string }>({
    text: 'Sign Up to Download',
    link: pricingUrl,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [faqs, setFaqs] = useState(null);

  useEffect(() => {
    AbstractAPI.request('/ppro-faqs', AbstractAPI.buildFetchOptions()).then(
      (faqs) => {
        faqs
          .json()
          .then((data) => {
            setFaqs(
              data.map((faq) => {
                return {
                  sectionName: faq.title,
                  title: faq.title,
                  text: faq.body,
                };
              })
            );
            setLoading(false);
          })
          .catch(() => {
            setFaqs(null);
            setLoading(false);
          });
      }
    );
  }, []);

  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const hasAnySubscription = useSelector(selectHasAnySubscription);
  const upgradeUrl = useUpgradeUrl();

  useEffect(() => {
    if (pproPluginEnabled) {
      // user with plugin access
      setCTAInfo({
        text: 'Download Now',
        link: ADOBE_PLUGIN_MARKETPLACE_URL,
      });
    } else if (isLoggedIn) {
      // user without plugin access
      setCTAInfo({
        text: 'Get Started',
        link: hasAnySubscription ? upgradeUrl : pricingUrl,
      });
    } else {
      setCTAInfo({
        text: 'Sign Up to Download',
        link: pricingUrl,
      });
    }
  }, [pproPluginEnabled, isLoggedIn, hasAnySubscription]);

  const renderFaqs = () => {
    return faqs && faqs.length > 0 ? (
      <GenericFAQ
        title="Frequently Asked Questions"
        sections={faqs}
        showLearnMore={false}
      />
    ) : null;
  };

  return (
    <>
      <ImageAndText
        classNames="bg-gray-950"
        darkMode={true}
        h1Header={true}
        heading="Unify your creative tools for a smarter workflow"
        content="Stay focused on your projects and work faster with the Storyblocks plugin for Adobe Creative Cloud."
        showCallToAction={true}
        callToActionColor={ButtonColor.Primary}
        callToActionText={ctaInfo.text}
        callToActionHref={ctaInfo.link}
        imageSrc="/assets/common/images/PremiereProPlugin/Adobe-LP-Hero-Image.jpg"
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/PremiereProPlugin/Premiere-Pro-Icon.svg',
          '/assets/common/images/PremiereProPlugin/After-Effects-Icon.svg',
        ]}
        sectionIconAlt={['Premiere Pro', 'After Effects']}
      />
      <ImageAndText
        heading="Ditch the browser"
        content="Search, preview, and download assets from the Storyblocks library without leaving your editor, saving you valuable time."
        imagesFirst={true}
        imageSrc="/assets/common/images/PremiereProPlugin/DitchTheBrowser.gif"
        imageSourceAlt="A view of stock video content on the Premiere Pro Plugin."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/PremiereProPlugin/DitchTheBrowserIcon.svg',
        ]}
        sectionIconAlt={['Ditch the browser']}
      />
      <ImageAndText
        heading="Make videos faster"
        content="Access saved content directly in your editors, making it easier to source the right assets for your video. Plus, use custom templates or music recommendations to find inspiration and kick-start any project."
        imageSrc="/assets/common/images/PremiereProPlugin/OptimizeYourWorkflow.gif"
        imageSourceAlt="A view of completed downloads on the Premiere Pro Plugin."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/PremiereProPlugin/OptimizeYourWorkflowIcon.svg',
        ]}
        sectionIconAlt={['Optimize your workflow']}
      />
      <ImageAndText
        heading="Create with confidence"
        content="Easily test content and iterate on video concepts with unlimited downloads from our diverse, royalty-free library, all backed by our comprehensive license."
        imagesFirst={true}
        imageSrc="/assets/common/images/PremiereProPlugin/CreateWithConfidence.gif"
        imageSourceAlt="A view of the Premiere Pro Plugin content type filter, an image search result, and an audio search result."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/PremiereProPlugin/CreateWithConfidenceIcon.svg',
        ]}
        sectionIconAlt={['Create with confidence']}
      />
      <UnlockCreativeFreedom
        subHeading="Work smarter"
        heading="Unlock creative freedom"
        content="Available to Storyblocks for Business and Pro plan members through Adobe's marketplace"
        imageSrc="/assets/common/images/PremiereProPlugin/UnlockCreativeFreedom.png"
        imageSourceAlt="A view of the Premiere Pro Plugin open in Premiere Pro"
        showCallToAction={true}
        callToActionColor={ButtonColor.Primary}
        callToActionText={ctaInfo.text}
        callToActionHref={ctaInfo.link}
      />
      <div className="p-8 maker-md:p-16 lg:p-24">
        <div className="flex flex-col justify-center items-center maker-md:flex-row maker-md:items-stretch">
          <MultilineTipsAndTutorialsCard
            href={`${SiteConstants.getBlogUrl(
              BlogResourceTypes.News
            )}/storyblocks-plugin-for-adobe-creative-cloud`}
            src="/assets/common/images/PremiereProPlugin/announcement-ae.png"
            thumbnailAlt="Storyblocks x Adobe Premiere Pro"
            category="Blog"
            text="Meet the Storyblocks Plugin for Adobe Creative Cloud"
          />
          <MultilineTipsAndTutorialsCard
            href="https://www.youtube.com/watch?v=hO46DUyuB8A"
            src={'/assets/common/images/PremiereProPlugin/how-to-install.png'}
            thumbnailAlt="Person using computer"
            category="Tutorial"
            text="How to install the Storyblocks plugin"
          />
          <MultilineTipsAndTutorialsCard
            href="https://www.youtube.com/watch?v=hElpMBWUIFA"
            src={
              '/assets/common/images/PremiereProPlugin/adobe-plugin-tips.png'
            }
            thumbnailAlt="Screenshot of the Storyblocks Adobe Creative Cloud Plugin"
            category="Tips"
            text="Top 5 After Effects templates in the plugin"
          />
        </div>
      </div>
      {loading ? <LoadingIndicator /> : renderFaqs()}
    </>
  );
};

export default PremiereProPluginLandingPageContainer;
